import { Box, Stack } from "@mui/system";
import { Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { spacing } from "../../../../../../../../../assets/styles/tokens";
import BudgetControlCardItemView from "./BudgetControlCardItemView";

// ─── Formatters (mirror Budget Control Desktop exactly) ──────────────────────
// Null/undefined metric values are shown as a dash, never zero.
const DASH = "-";

// Date → DD/MM/YYYY (mirrors BizLeader Dashboard Mobile > Brands last-run)
const formatDate = (dateString: string | null): string => {
  if (!dateString) return DASH;
  return new Date(dateString).toLocaleDateString("en-GB");
};

const cleanNumber = (value: any): number =>
  parseFloat(String(value ?? "0").replace(/[% ,]/g, "")) || 0;

// Plain text — dash when missing
const formatText = (value: any): string =>
  value === null || value === undefined || value === "" ? DASH : String(value);

// Money: rounded, 1000-separated, negative as -$100, positive as $100 (dash when missing)
const formatMoney = (value: any): string => {
  if (value === undefined || value === null || value === "") return DASH;
  const num = Number(String(value).replace(/[, ]/g, ""));
  if (isNaN(num)) return DASH;
  const formatted = Math.abs(num).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });
  return num < 0 ? `-$${formatted}` : `$${formatted}`;
};

// Percent badge text — API returns decimals (0.955 = 95.5%), 1 decimal place (dash when missing)
const formatPercent = (value: any): string => {
  if (value === null || value === undefined) return DASH;
  return `${(cleanNumber(value) * 100).toFixed(1)}%`;
};

//rounded down to the nearest integer(https://optival.atlassian.net/browse/GR-21152)
// QFTD budget: rounded down to nearest integer; empty when missing
const roundDownBudget = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  return Number.isFinite(num) ? String(Math.floor(num)) : "";
};

// Truncate toward zero (down for positive, up for negative); (https://optival.atlassian.net/browse/GR-21152)
// Remaining QFTD: truncated toward zero (down if positive, up if negative); empty when missing
const truncTowardZero = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(String(value).replace(/[, ]/g, ""));
  return Number.isFinite(num) ? String(Math.trunc(num)) : "";
};

// Whole-ish number with up to 2 decimals (e.g. 300 or 52.5) (dash when missing)
// const formatTwoDecimal = (value: any): string =>
//   value != null && value !== ""
//     ? String(parseFloat(Number(value).toFixed(2)))
//     : DASH;

// % QFTD Budget Used — computed as qftd / qftd_budget (decimal ratio, e.g. 0.963)
// Returns null when either side is missing/zero so the chip renders a dash.
const qftdUsedDecimal = (qftd: any, qftdBudget: any): number | null => {
  if (qftd === null || qftd === undefined || qftd === "") return null;
  if (qftdBudget === null || qftdBudget === undefined || qftdBudget === "")
    return null;
  const budget = cleanNumber(qftdBudget);
  if (budget === 0) return null;
  return cleanNumber(qftd) / budget;
};

// Highlight rules (mirror Budget Control Desktop)
const percentValue = (value: any): number => cleanNumber(value) * 100;

// % QFTD Budget Used: red if ≥100%, else green
const qftdStatus = (value: any): string =>
  percentValue(value) >= 100 ? "error" : "success";

// % Budget Used: red if ≥100%, yellow if 80–99%, else green
const budgetStatus = (value: any): string => {
  const percent = percentValue(value);
  if (percent >= 100) return "error";
  if (percent >= 80) return "warning";
  return "success";
};

// % of RR Budget Used: red if ≥100%, else green
const rrBudgetStatus = (value: any): string =>
  percentValue(value) >= 100 ? "error" : "success";
// ─────────────────────────────────────────────────────────────────────────────

const BudgetControlCardItem = ({ data = [] }: { data?: any[] }) => {
  // Brand last-run data — keyed by brand_id (mirrors BizLeader Brands)
  const lastRunData: any[] =
    useSelector(
      (state: any) =>
        state.bizDevelopmentDashboardMobile.budgetBrandPerformanceLastRunData
          ?.query_output,
    ) ?? [];

  const lastRunMap = new Map(
    lastRunData.map((run: any) => [String(run.brand_id), run]),
  );

  if (!data || data.length === 0) {
    return (
      <Box sx={{ mt: "-" + spacing.Spacing.md.$value, py: 4 }}>
        <Typography textAlign="center" fontSize="14px" color="text.secondary">
          No data found
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mt: "-" + spacing.Spacing.md.$value }}>
        <Stack spacing={2}>
          {data.map((item: any, index: number) => {
            // No brand_id on the card → no last-run match possible, dash all fields
            const hasBrandId =
              item.brand_id !== null &&
              item.brand_id !== undefined &&
              item.brand_id !== "";
            const lastRun: any = hasBrandId
              ? lastRunMap.get(String(item.brand_id))
              : undefined;
            const runDate = lastRun ? formatDate(lastRun.created) : DASH;
            const runRange = lastRun
              ? `${formatDate(lastRun.from_date)} - ${formatDate(
                  lastRun.to_date,
                )}`
              : DASH;
            const runBy = lastRun?.run_by || DASH;
            const status = !hasBrandId
              ? DASH
              : lastRun?.status === "SUCCESS"
                ? "Success"
                : "Failure";
            return (
              <BudgetControlCardItemView
                key={item.brand_id ?? index}
                website_name={formatText(item.brand_display_name)}
                website_id={formatText(item.brand_id)}
                qftd={formatText(item.qftd)}
                qftds={roundDownBudget(item.qftd_budget)}
                qftd_status={qftdStatus(
                  qftdUsedDecimal(item.qftd, item.qftd_budget),
                )}
                qftd_percentage={formatPercent(
                  qftdUsedDecimal(item.qftd, item.qftd_budget),
                )}
                budget_dollar={formatMoney(item.cpa_income)}
                budgets_dollar={formatMoney(item.cpa_constraint)}
                budget_status={budgetStatus(item.perc_of_qftd_budget_used)}
                budget_percentage={formatPercent(item.perc_of_qftd_budget_used)}
                rr_budget_status={rrBudgetStatus(
                  item.perc_of_qftd_RR_budget_use,
                )}
                rr_budget_percentage={formatPercent(
                  item.perc_of_qftd_RR_budget_use,
                )}
                remaining_qftd={truncTowardZero(item.remaining_qftd)}
                cpa_income_dollar={formatMoney(item.cpa_income)}
                total_income_dollar={formatMoney(item.total_income)}
                cpa_budget_dollar={formatMoney(item.cpa_constraint)}
                ftd={formatText(item.ftd)}
                runDate={runDate}
                runRange={runRange}
                runBy={runBy}
                status={status}
              />
            );
          })}
        </Stack>
      </Box>
    </>
  );
};

export default BudgetControlCardItem;
